import { useState, useMemo } from 'react'
import {
  Box, Typography, Chip, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Paper,
} from '@mui/material'
import { SwapHoriz, Search } from '@mui/icons-material'
import ExportMenu from '../../components/common/ExportMenu'

const DEMO_DATA = [
  { line: 'Line 1', runDays: 22, changeoverHrs: 18.0, availableHrs: 352.0, scheduledHrs: 334.0 },
  { line: 'Line 2', runDays: 21, changeoverHrs: 12.5, availableHrs: 336.0, scheduledHrs: 310.0 },
  { line: 'Line 3', runDays: 20, changeoverHrs: 24.0, availableHrs: 320.0, scheduledHrs: 288.0 },
  { line: 'Line 4', runDays: 18, changeoverHrs: 8.0, availableHrs: 288.0, scheduledHrs: 264.0 },
]

export default function ChangeoverTimes() {
  const [search, setSearch] = useState('')
  const [orderBy, setOrderBy] = useState('line')
  const [order, setOrder] = useState('asc')

  const handleSort = (col) => {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(col)
  }

  const rows = useMemo(() => {
    let filtered = DEMO_DATA.filter((r) =>
      r.line.toLowerCase().includes(search.toLowerCase()),
    )
    filtered.sort((a, b) => {
      const av = a[orderBy], bv = b[orderBy]
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
      return order === 'asc' ? cmp : -cmp
    })
    return filtered
  }, [search, orderBy, order])

  const columns = [
    { id: 'line', label: 'Line' },
    { id: 'runDays', label: 'Run Days', align: 'right' },
    { id: 'changeoverHrs', label: 'Changeover Hours', align: 'right' },
    { id: 'availableHrs', label: 'Available Production Hours', align: 'right' },
    { id: 'scheduledHrs', label: 'Scheduled Hours', align: 'right' },
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <SwapHoriz color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Changeover Times</Typography>
        <ExportMenu />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="Change-Over Times by Line" size="small" variant="outlined" />
        <Typography variant="overline" color="text.secondary">
          As of Mar 10, 2026 · v3 · {DEMO_DATA.length} items
        </Typography>
      </Box>

      <TextField
        size="small"
        placeholder="Search lines…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 280 }}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
      />

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align || 'left'} sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.line} hover>
                <TableCell sx={{ fontWeight: 600 }}>{row.line}</TableCell>
                <TableCell align="right">{row.runDays}</TableCell>
                <TableCell align="right">{row.changeoverHrs.toFixed(1)}</TableCell>
                <TableCell align="right">{row.availableHrs.toFixed(1)}</TableCell>
                <TableCell align="right">{row.scheduledHrs.toFixed(1)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
